import { useMemo } from "react";
import { useBorrowerTransactionsQuery, type BorrowerTransaction } from "./queries";

export type { BorrowerTransaction } from "./queries";

export interface RecurringGroup {
  name: string;
  transactions: BorrowerTransaction[];
  count: number;
  totalAmount: number;
  avgAmount: number;
  stdDev: number;
  firstDate: string;
  lastDate: string;
}

export interface UseTransactionsReturn {
  transactions: BorrowerTransaction[];
  loading: boolean;
  error: string | null;
  subCategories: string[];
  recurringWithdrawals: RecurringGroup[];
  topWithdrawals: BorrowerTransaction[];
  recurringDeposits: RecurringGroup[];
  topDeposits: BorrowerTransaction[];
}

export function groupByName(txs: BorrowerTransaction[]): RecurringGroup[] {
  const map = new Map<string, BorrowerTransaction[]>();

  for (const tx of txs) {
    const key = (tx.counterparty_name?.trim() || tx.description.trim()).toLowerCase();
    const arr = map.get(key);
    if (arr) {
      arr.push(tx);
    } else {
      map.set(key, [tx]);
    }
  }

  const groups: RecurringGroup[] = [];
  map.forEach((groupTxs) => {
    if (groupTxs.length < 2) return;
    const sorted = [...groupTxs].sort((a, b) => a.date.localeCompare(b.date));
    const totalAmount = groupTxs.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
    const avgAmount = totalAmount / groupTxs.length;
    const variance =
      groupTxs.reduce((sum, tx) => sum + Math.pow(Math.abs(tx.amount) - avgAmount, 2), 0) /
      groupTxs.length;
    groups.push({
      name: groupTxs[0].counterparty_name?.trim() || groupTxs[0].description.trim(),
      transactions: sorted,
      count: groupTxs.length,
      totalAmount,
      avgAmount,
      stdDev: Math.sqrt(variance),
      firstDate: sorted[0].date,
      lastDate: sorted[sorted.length - 1].date,
    });
  });

  return groups.sort((a, b) => b.totalAmount - a.totalAmount);
}

export function topByPercentile(
  txs: BorrowerTransaction[],
  percentile: number
): BorrowerTransaction[] {
  if (txs.length === 0) return [];
  const sorted = [...txs].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const count = Math.max(1, Math.ceil(sorted.length * (1 - percentile / 100)));
  return sorted.slice(0, count);
}

export function useTransactions(
  borrowerId: number | null,
  source: "statements" | "plaid" = "statements"
): UseTransactionsReturn {
  const {
    data: transactions = [],
    isLoading: loading,
    error: queryError,
  } = useBorrowerTransactionsQuery(borrowerId, source);
  const error = (queryError as Error)?.message ?? null;

  const debits = useMemo(
    () => transactions.filter((tx) => tx.transaction_type === "debit"),
    [transactions]
  );

  const credits = useMemo(
    () => transactions.filter((tx) => tx.transaction_type === "credit"),
    [transactions]
  );

  const subCategories = useMemo(() => {
    const cats = new Set<string>();
    transactions.forEach((tx) => {
      if (tx.category) cats.add(tx.category);
    });
    return Array.from(cats).sort();
  }, [transactions]);

  const recurringWithdrawals = useMemo(() => groupByName(debits), [debits]);
  const recurringDeposits = useMemo(() => groupByName(credits), [credits]);
  const topWithdrawals = useMemo(() => topByPercentile(debits, 90), [debits]);
  const topDeposits = useMemo(() => topByPercentile(credits, 90), [credits]);

  return {
    transactions,
    loading,
    error,
    subCategories,
    recurringWithdrawals,
    topWithdrawals,
    recurringDeposits,
    topDeposits,
  };
}
